"use client";

import { SRM_MOCK_USERS } from "@/mocks/auth/auth-mocks";
import { ChevronsUpDown, User } from "lucide-react";
import React from "react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { Avatar, AvatarImage } from "@components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";
import { SidebarMenuButton, useSidebar } from "@components/ui/sidebar";
import { isLocalEnvironment } from "@lib/environment";
import { setAccessTokenFixtureFromObjectToCookies } from "@features/auth/utils/utils-server";
import routes from "@features/routes";

export const NavUserLogin = () => {
  const { isMobile } = useSidebar();
  const t = useTranslations("features.auth.ui.nav-user-login");

  const localLoginHandler = (user: object) => {
    setAccessTokenFixtureFromObjectToCookies(user).then(() => {
      window.location.reload();
    });
  };

  if (!isLocalEnvironment) {
    return (
      <SidebarMenuButton
        size="lg"
        className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
        asChild
      >
        <Link
          href={routes.auth.api.login({
            query: {
              redirect_uri: window.location.href,
            },
          })}
          prefetch={false}
        >
          <Avatar className="size-8 rounded-md">
            <AvatarImage src="/images/avatar-5.webp" alt="Unknown user" className="rounded-md" />
          </Avatar>
          <div className="grid flex-1 text-left text-sm leading-tight">
            <span className="truncate font-medium">{t("login")}</span>
          </div>
          <ChevronsUpDown className="ml-auto size-4" />
        </Link>
      </SidebarMenuButton>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <SidebarMenuButton
          size="lg"
          className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
        >
          <Avatar className="size-8 rounded-md">
            <AvatarImage src="/images/avatar-5.webp" alt="Unknown user" className="rounded-md" />
          </Avatar>
          <div className="grid flex-1 text-left text-sm leading-tight">
            <span className="truncate font-medium">{t("login")}</span>
          </div>
          <ChevronsUpDown className="ml-auto size-4" />
        </SidebarMenuButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-md"
        side={isMobile ? "bottom" : "right"}
        align="end"
        sideOffset={4}
      >
        <DropdownMenuLabel className="p-0 font-normal">
          <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
            <Avatar className="size-8 rounded-md">
              <AvatarImage src="/images/avatar-5.webp" alt="Unknown user" className="rounded-md" />
            </Avatar>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-medium">{t("local-authorization")}</span>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          {Object.entries(SRM_MOCK_USERS).map(([id, user]) => (
            <DropdownMenuItem key={id} onClick={() => localLoginHandler(user)}>
              <User />
              {id}
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
